import React from 'react';
import { Box, Grid2 } from '@mui/material';

import { Formik } from 'formik';
import * as Yup from 'yup';

import CustomDialog from '../lib/ui-components/Dialog/CustomDialog';
import CustomDialogContent from '../lib/ui-components/Dialog/CustomDialogContent';
import CustomTypography from '../lib/ui-components/Typography/CustomTypography';
import CustomSelect from '../lib/ui-components/Select/CustomSelect';
import CustomTextField from '../lib/ui-components/TextField/CustomTextField';
import CustomDialogActions from '../lib/ui-components/Dialog/CustomDialogActions';
import CustomButton from '../lib/ui-components/Button/CustomButton';
import { type OnHoldConsultMetadataResponse } from './statusRequestService';

export interface OnHoldReasonFacilityParams {
  onHoldConsultReasonId: number;
  onHoldConsultSubReasonId: number;
  onHoldConsultOtherReason: string;
}

const onHoldConsultinitialValues: OnHoldReasonFacilityParams = {
  onHoldConsultReasonId: 0,
  onHoldConsultSubReasonId: 0,
  onHoldConsultOtherReason: '',
};

export interface OnHoldConsultConfirmationPopupProps {
  openOnHoldConsultPopup: boolean;
  onCloseOnHoldConsultPopup: () => void;
  ConsultOnHoldMetaData: OnHoldConsultMetadataResponse | undefined;
  onSubmitOnHoldConsult: (data: OnHoldReasonFacilityParams) => void;
  initialValues?: OnHoldReasonFacilityParams | null;
  portal?: string;
}

const OnHoldConsultModel: React.FC<OnHoldConsultConfirmationPopupProps> = ({
  openOnHoldConsultPopup,
  onCloseOnHoldConsultPopup,
  ConsultOnHoldMetaData,
  onSubmitOnHoldConsult,
  initialValues,
  portal,
}) => {
  const [onHoldConsultValues, setOnHoldConsultValues] =
    React.useState<OnHoldReasonFacilityParams>(onHoldConsultinitialValues);

  const subReasonRequiredIds =
    ConsultOnHoldMetaData?.onHoldReasonList
      ?.filter((x) => x.subReason?.length > 0)
      .map((x) => x.id) || [];

  const otherReasonId = ConsultOnHoldMetaData?.onHoldReasonList?.find(
    (x) => x.name.toLowerCase() === 'other'
  )?.id;

  const validationReasonSchema = Yup.object({
    onHoldConsultReasonId: Yup.number()
      .transform((_, val) => (val === 0 ? undefined : Number(val)))
      .required('On Hold reason is required')
      .typeError('On Hold reason is required'),

    onHoldConsultSubReasonId: Yup.number().when(
      'onHoldConsultReasonId',
      ([onHoldConsultReasonId], schema) =>
        subReasonRequiredIds.includes(onHoldConsultReasonId)
          ? schema
              .min(1, 'Sub reason is required')
              .required('Sub reason is required')
          : schema.notRequired()
    ),

    onHoldConsultOtherReason: Yup.string().when(
      'onHoldConsultReasonId',
      ([onHoldConsultReasonId], schema) =>
        onHoldConsultReasonId === otherReasonId
          ? schema.trim().required('Please enter details')
          : schema.notRequired()
    ),
  });

  React.useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setOnHoldConsultValues(initialValues);
    }
  }, []);
  const handleSubmitConsultOnHoldReason = async (
    cancelReasonData: typeof onHoldConsultinitialValues
  ) => {
    onSubmitOnHoldConsult(cancelReasonData);
  };

  return (
    <Box>
      <Formik
        initialValues={onHoldConsultValues}
        validateOnMount={true}
        enableReinitialize
        validateOnChange
        validationSchema={validationReasonSchema}
        onSubmit={(values) => {
          handleSubmitConsultOnHoldReason(values);
        }}
      >
        {({
          setFieldValue,
          values,
          handleSubmit,
          resetForm,
          errors,
          touched,
          validateField,
        }) => {
          const selectedReason = ConsultOnHoldMetaData?.onHoldReasonList?.find(
            (item) => item.id === values.onHoldConsultReasonId
          );

          const hasSubReasons =
            selectedReason && selectedReason?.subReason?.length > 0;
          const isOtherReason = selectedReason?.name === 'Other';
          return (
            <CustomDialog
              id="onhold-consult-dialog"
              open={openOnHoldConsultPopup}
              onClose={() => {
                onCloseOnHoldConsultPopup();
                resetForm();
              }}
              disableEscapeKeyDown={true}
            >
              <CustomDialogContent className="dialog-width">
                <Box className="pb-6">
                  <CustomTypography
                    id="onhold-consult-title-popup"
                    variant="subtitle1"
                  >
                    On Hold Consult
                  </CustomTypography>
                </Box>
                <Box>
                  <CustomTypography id="onhold-title-popup" variant="body3">
                    Are you sure you want to On Hold this consult?
                  </CustomTypography>
                  {!portal && (
                    <CustomTypography id="onhold-title-popup" variant="body3">
                      This action cannot be undone.
                    </CustomTypography>
                  )}
                </Box>

                <Box className="mt-16">
                  <CustomSelect
                    id="select2"
                    placeholder="Select On Hold Reason"
                    options={ConsultOnHoldMetaData?.onHoldReasonList || []}
                    selectedValue={values.onHoldConsultReasonId}
                    onChange={(e) => {
                      const value = Number(e);
                      setFieldValue('onHoldConsultReasonId', value, false);

                      setTimeout(() => {
                        validateField('onHoldConsultReasonId');
                      }, 0);
                      setFieldValue('onHoldConsultSubReasonId', 0, true);
                      setFieldValue('onHoldConsultOtherReason', '', true);
                    }}
                    size="small"
                    displayEmpty={true}
                    fullWidth
                  />
                  {errors?.onHoldConsultReasonId &&
                    touched?.onHoldConsultReasonId && (
                      <CustomTypography
                        id="validate-onHoldReason"
                        color="error"
                        variant="body3"
                        className="pt-8"
                      >
                        {errors.onHoldConsultReasonId}
                      </CustomTypography>
                    )}
                </Box>
                {hasSubReasons && (
                  <Box className="mt-16">
                    <CustomSelect
                      id="sub-reason"
                      placeholder="Select Sub Reason"
                      options={selectedReason?.subReason || []}
                      selectedValue={values.onHoldConsultSubReasonId}
                      onChange={(e) => {
                        const value = e;
                        setFieldValue(
                          'onHoldConsultSubReasonId',
                          Number(value)
                        );
                      }}
                      size="small"
                      displayEmpty={true}
                      fullWidth
                    />

                    {errors?.onHoldConsultSubReasonId &&
                      touched?.onHoldConsultSubReasonId && (
                        <CustomTypography
                          id="validate-onHoldReason"
                          color="error"
                          variant="body3"
                          className="pt-8"
                        >
                          {errors.onHoldConsultSubReasonId}
                        </CustomTypography>
                      )}
                  </Box>
                )}

                {isOtherReason && (
                  <Box className="mt-16">
                    <CustomTextField
                      placeholder="Type Details"
                      id=""
                      value={values.onHoldConsultOtherReason}
                      onChange={(e) =>
                        setFieldValue(
                          'onHoldConsultOtherReason',
                          e.target.value
                        )
                      }
                      fullWidth
                      multiLine
                    />

                    {errors?.onHoldConsultOtherReason &&
                      touched?.onHoldConsultOtherReason && (
                        <CustomTypography
                          id="validate-onHoldReason"
                          color="error"
                          variant="body3"
                          className="pt-8"
                        >
                          {errors.onHoldConsultOtherReason}
                        </CustomTypography>
                      )}
                  </Box>
                )}
              </CustomDialogContent>

              <CustomDialogActions>
                <Grid2 container>
                  <Grid2 className="mr-8">
                    <CustomButton
                      id="btnYes"
                      color="error"
                      variant="outlined"
                      onClick={() => {
                        onCloseOnHoldConsultPopup();
                        resetForm();
                      }}
                    >
                      Close
                    </CustomButton>
                  </Grid2>
                  <Grid2>
                    <CustomButton
                      id="btnConfirm-complete"
                      color="primary"
                      variant="contained"
                      onClick={() => handleSubmit()}
                    >
                      Submit
                    </CustomButton>
                  </Grid2>
                </Grid2>
              </CustomDialogActions>
            </CustomDialog>
          );
        }}
      </Formik>
    </Box>
  );
};

export default OnHoldConsultModel;
